import type { CategoryOption } from '../domain/categoryOptions';

export type BulkCategoryScope =
  | { mode: 'all' }
  | { mode: 'only'; categoryIds: string[] }
  | { mode: 'all_except'; categoryIds: string[] };

export type BulkCategoryScopeMode = BulkCategoryScope['mode'];

export const DEFAULT_BULK_CATEGORY_SCOPE: BulkCategoryScope = { mode: 'all' };

export type BulkScopeModeOption = { id: BulkCategoryScopeMode; label: string };

export const BULK_SCOPE_MODE_OPTIONS: BulkScopeModeOption[] = [
  { id: 'all', label: 'Todo el catálogo' },
  { id: 'all_except', label: 'Todo excepto…' },
  { id: 'only', label: 'Solo estas categorías…' },
];

export function bulkCategoryScopeMode(scope: BulkCategoryScope): BulkCategoryScopeMode {
  return scope.mode;
}

/** Compat: filtro de listado catálogo → alcance «solo estas». */
export function bulkCategoryScopeFromCatalogFilter(categoryId: string | 'all' | null | undefined): BulkCategoryScope {
  if (!categoryId || categoryId === 'all') return DEFAULT_BULK_CATEGORY_SCOPE;
  return { mode: 'only', categoryIds: [categoryId] };
}

export function categoryMatchesBulkScope(categoryId: string | null | undefined, scope: BulkCategoryScope): boolean {
  const cid = String(categoryId ?? '');
  if (scope.mode === 'all') return true;
  if (scope.mode === 'only') return scope.categoryIds.includes(cid);
  return !scope.categoryIds.includes(cid);
}

export function isBulkCategoryScopeReady(scope: BulkCategoryScope): boolean {
  if (scope.mode === 'only' || scope.mode === 'all_except') return scope.categoryIds.length > 0;
  return true;
}

export function bulkCategoryScopeModeLabel(mode: BulkCategoryScopeMode): string {
  return BULK_SCOPE_MODE_OPTIONS.find((o) => o.id === mode)?.label ?? 'Todo el catálogo';
}

function categoryLabelById(categoryOptions: CategoryOption[], id: string): string {
  return categoryOptions.find((o) => o.id === id)?.label ?? 'Categoría';
}

export function bulkCategoryScopeSummary(
  scope: BulkCategoryScope,
  categoryOptions: CategoryOption[],
): string {
  if (scope.mode === 'all') return 'Todo el catálogo';
  const names = scope.categoryIds.map((id) => categoryLabelById(categoryOptions, id));
  if (names.length === 0) {
    return scope.mode === 'only' ? 'Elegí categorías' : 'Elegí qué excluir';
  }
  if (names.length === 1) {
    return scope.mode === 'only' ? names[0]! : `Excepto ${names[0]!}`;
  }
  if (names.length === 2) {
    return scope.mode === 'only' ? `${names[0]} y ${names[1]}` : `Excepto ${names[0]} y ${names[1]}`;
  }
  const n = names.length;
  return scope.mode === 'only' ? `${n} categorías` : `Excepto ${n} categorías`;
}

export function bulkCategoryScopeForAudit(scope: BulkCategoryScope): Record<string, unknown> {
  if (scope.mode === 'all') return { mode: 'all' };
  return { mode: scope.mode, categoryIds: [...scope.categoryIds] };
}
